<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1">Rental products</h1>
            <p class="text-muted mb-0">The rooms, units and items you rent out</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew()">
              <CIcon name="cil-plus" /> Add product
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
        </div>
        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6">Couldn't load your rental products</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" variant="outline" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-home" size="xl" class="text-muted mb-2" />
          <h2 class="h6">No rental products yet</h2>
          <p class="text-muted">Add the rooms, units or items you rent out so you can take bookings.</p>
          <CButton color="primary" @click="addNew()">
            <CIcon name="cil-plus" /> Add product
          </CButton>
        </div>
        <template v-else>
          <div class="d-none d-lg-block">
            <CDataTable
              :items="items"
              :fields="fields"
              column-filter
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #show_index="{ index }">
                <td class="py-2">
                  {{ index + 1 }}
                </td>
              </template>
              <template #show_details="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    color="primary"
                    variant="ghost"
                    :aria-label="`Edit ${item.name}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="danger"
                    variant="ghost"
                    :aria-label="`Delete ${item.name}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>
          <div class="d-lg-none">
            <CCard v-for="item in items" :key="item.id" class="mb-2">
              <CCardBody class="p-3 d-flex justify-content-between align-items-start">
                <div>
                  <div class="font-weight-bold">{{ item.name }}</div>
                  <div class="small text-muted">{{ item.type }}</div>
                  <div v-if="item.description" class="small text-muted">{{ item.description }}</div>
                </div>
                <div class="text-nowrap">
                  <CButton
                    color="primary"
                    variant="ghost"
                    :aria-label="`Edit ${item.name}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="danger"
                    variant="ghost"
                    :aria-label="`Delete ${item.name}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal :show.sync="warningModal" :centered="true" :close-on-backdrop="!deleting">
      <template #header>
        <h5 class="modal-title">Delete {{ itemToDelete.name }}?</h5>
      </template>
      <p class="mb-0">
        {{ itemToDelete.name }} will be removed and can no longer be booked. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete product
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import RentalProductApi from "@/lib/rentalProductApi";

export default {
  name: "RentalProductList",
  data() {
    return {
      loading: true,
      error: false,
      deleting: false,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
        { key: "name", label: "Name" },
        { key: "type", label: "Type" },
        { key: "description", label: "Description" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],
      api: new RentalProductApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Rental & Leasing", "Products"]);
    this.refreshTable();
  },
  methods: {
    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;
      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh your rental products. Check your connection and try again.");
          }
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({
        path: `/tenants/RentalProduct/${item.id}`,
      });
    },
    confirmDelete() {
      var self = this;
      self.deleting = true;
      this.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.$toast.success("Rental product deleted");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't delete the product. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    addNew() {
      this.$router.push({ path: "/tenants/RentalProduct" });
    },
  },
};
</script>
